<script>
  import Button from '@leaflink/stash/Button.vue';
  import Chip from '@leaflink/stash/Chip.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Select from '@leaflink/stash/Select.vue';
  import cloneDeep from 'lodash-es/cloneDeep';
  import isEmpty from 'lodash-es/isEmpty';
  import uniq from 'lodash-es/uniq';
  import { storeToRefs } from 'pinia';
  import { mapActions } from 'vuex';

  import { LicensePicker } from '@/components/LicensePicker';
  import RequestSample from '@/pages/Shop/_components/RequestSample.vue';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { trackBuyerModalProductAdded } from '@/segment';
  import { getVarietyPriceRange } from '@/utils/commerce';
  import { notifyError } from '@/utils/errors';
  import { money } from '@/utils/i18n';
  import { encodeSegmentTrackPageDataToQueryParams } from '@/utils/segmentTrackPageData';

  import { hasBaseUnitPricing, isParent } from '../../utils';
  import ProductDetail from './ProductDetail.vue';
  import VolumeDiscountListItem from './VolumeDiscountListItem.vue';

  const emptyBuyer = {
    id: '',
    inputs: {
      quantity: '',
      notes: '',
    },
    showNotes: false,
    errors: {},
  };

  export default {
    name: 'ProductDetailPricing',

    components: {
      Button,
      Chip,
      Icon,
      Input,
      LicensePicker,
      Select,
      ProductDetail,
      VolumeDiscountListItem,
      RequestSample,
    },

    props: {
      brandId: {
        type: [Number, String],
        required: true,
      },

      companySlug: {
        type: String,
        required: false,
        default: '',
      },

      isPreview: Boolean,

      productId: {
        type: [Number, String],
        required: true,
      },

      preselectedVarietyId: {
        type: [Number, String],
        required: true,
      },

      /**
       * For the "source" property of the tracking event.
       * Ex: "PDP Modal"
       */
      trackingSource: {
        type: String,
        required: true,
      },

      /**
       * For Segment tracking purposes, indicates the context in which the modal was opened
       */
      source: {
        type: String,
        required: true,
      },
    },

    emits: ['close', 'add', 'change', 'bulk-add', 'refresh-product'],

    data() {
      return {
        buyers: [cloneDeep(emptyBuyer)],
        isAddingToCarts: false,
        product: undefined,
        // Equal to this.product, or a variety (when this.product is a parent AND preselectedVarietyId is not set)
        selectedVariety: undefined,
      };
    },

    computed: {
      /**
       * Returns the product being displayed (variety, parent, or standalone)
       */
      displayedProduct() {
        return this.selectedVariety || this.product;
      },

      /**
       * Disable Quantity and Notes inputs
       */
      shouldDisableInputs() {
        return !this.displayedProduct.shouldBeVisible || this.isPreview;
      },

      // Determines when the Add to Cart button is enabled
      canAddToCart() {
        const { buyers, isParent, product, selectedVariety } = this;

        return !!(
          !this.isAddingToCarts &&
          // variety must be selected when product is a parent
          ((isParent(product) && selectedVariety) || !isParent(product)) &&
          // at least one buyer block must have a quantity
          // allowing some to have 0 quantity as another way to delete the buyer block
          buyers.some((buyer) => buyer.inputs.quantity) &&
          // all buyer blocks must have a location selected
          buyers.every((buyer) => buyer.id)
        );
      },

      // Whether to show the price per unit (for a product, or product varieties)
      // Product varieties need to all be sold in bulk (cases), and all have matching units of measure.
      showPricePerUnit() {
        const { hasSameVarietyUnitsOfMeasure, isParent, product } = this;

        return (
          (!isParent(product) && product.unitMultiplier > 1) ||
          (isParent(product) && hasSameVarietyUnitsOfMeasure && product.children.every((c) => c.unitMultiplier > 1))
        );
      },

      /**
       * Whether to a show price range for a parent product with varieties (no selected variety)
       */
      showPriceRange() {
        const { isParent, product, selectedVariety } = this;

        return isParent(product) && !selectedVariety;
      },

      // Whether or not product varieties have matching units of measure
      hasSameVarietyUnitsOfMeasure() {
        const { isParent, product } = this;

        return isParent(product) && uniq(product.children.map((i) => i.unitOfMeasure)).length === 1;
      },

      // Determines the price per unit. This is a range when variety unit prices differ.
      unitPrice() {
        const { displayedProduct, product, showPriceRange } = this;

        const price = showPriceRange
          ? getVarietyPriceRange('pricePerUnit', product)
          : money(displayedProduct.pricePerUnit);

        return `${price}/${this.$t('common.unit')}`;
      },

      // Verify the user and product are set up for requesting samples.
      // Verify the product in focus is not a parent product (in case a variety is not selected)
      canRequestSample() {
        return (
          this.permissions.canRequestSamples &&
          this.displayedProduct.isAvailableForSamples &&
          !(this.isParent(this.product) && !this.selectedVariety)
        );
      },

      allowedBuyers() {
        return this.product?.allowedBuyers || [];
      },
    },

    setup() {
      const shopStore = useShopStore();

      const { getIsCampaignLive } = storeToRefs(shopStore);

      const userStore = useUserStore();

      const { user, userActiveCompany, userActiveCompanyPermissions } = storeToRefs(userStore);

      return {
        activeCompany: userActiveCompany,
        algoliaQueryId: user.value.algoliaQueryId,
        companies: user.value.companies,
        getIsCampaignLive,
        permissions: userActiveCompanyPermissions,
      };
    },

    watch: {
      // Assign product that matches `preselectedVarietyId` to `selectedVariety`, when defined.
      'product.id'(id) {
        if (id && this.preselectedVarietyId && this.isParent(this.product)) {
          this.selectedVariety = this.product.children.find((child) => child.id === this.preselectedVarietyId);
        }
      },
    },

    mounted() {
      this.buyers[0].id = this.activeCompany.id;
    },

    methods: {
      getVarietyPriceRange,
      isParent,
      hasBaseUnitPricing,
      money,
      encodeSegmentTrackPageDataToQueryParams,

      ...mapActions({
        addToCart: 'cart/addToCart',
      }),

      /**
       * Determines if a given field has an error
       * @param {object} buyer - the buyer checked for errors
       * @param {string} field - the field being checked
       * @returns {boolean}
       */
      hasError(buyer, field) {
        return buyer.errors?.[field]?.length;
      },

      formattedErrors(buyer) {
        if (isEmpty(buyer.errors)) {
          return [];
        }

        return Object.values(buyer.errors).flat();
      },

      /**
       * Returns a list of available buyer options
       */
      getBuyerOptions(currentBuyer) {
        const { allowedBuyers } = this.product;
        const alreadySelectedBuyerIds = this.buyers.map((buyer) => buyer.id);

        return allowedBuyers.filter(
          (buyer) => buyer.id === currentBuyer.id || !alreadySelectedBuyerIds.includes(buyer.id),
        );
      },

      removeBuyers(buyerIds) {
        const filteredBuyers = this.buyers.filter((buyer) => !buyerIds.includes(buyer.id));

        this.buyers = filteredBuyers;
      },

      /**
       * Adds a new buyer block with no buyer specified
       */
      addNewBuyer() {
        this.buyers.push(cloneDeep(emptyBuyer));
      },

      /**
       * Add product to cart
       */
      addProductToCart(productId, companyId) {
        const buyer = this.buyers.find((buyer) => buyer.id === companyId);

        buyer.errors = {};

        return this.addToCart({ ...buyer.inputs, productId, companyId }).catch((error) => {
          const { request } = error || {};

          if (request?.status === 400 && error?.response?.data) {
            const { errors } = error.response?.data || {};

            buyer.errors = errors?.reduce((acc, error) => {
              const field = error.source.pointer.substring(1);

              if (!acc[field]) {
                acc[field] = [];
              }

              acc[field].push(error.detail);
              return acc;
            }, {});
          } else {
            throw error;
          }
        });
      },

      /**
       * Add product to carts
       */
      async addProductToCarts() {
        const productId = this.selectedVariety?.id || this.product.id;

        // remove any buyers with empty quantity or empty buyer id
        const filteredBuyers = this.buyers.filter((buyer) => buyer.inputs.quantity && buyer.id);

        this.buyers = filteredBuyers;
        this.isAddingToCarts = true;

        const successfulBuyerIds = [];
        const nonValidationErrors = [];
        let hasValidationErrors = false;

        try {
          await Promise.all(
            this.buyers.map((buyer) => {
              return this.addProductToCart(productId, buyer.id).then(
                (response) => {
                  if (response) {
                    successfulBuyerIds.push(response.company);
                  } else {
                    hasValidationErrors = true;
                  }
                },
                (error) => nonValidationErrors.push(error),
              );
            }),
          );

          const buyersString = successfulBuyerIds.join(',');

          trackBuyerModalProductAdded({
            brand_id: this.product.brand.id,
            buyerId: buyersString,
            sellerId: this.product.brand.company?.id,
            productId,
            query_id: this.algoliaQueryId,
            items: [{ id: String(productId) }],
          });

          this.removeBuyers(successfulBuyerIds);
          this.$emit('add', { shouldCloseModal: !hasValidationErrors });

          nonValidationErrors.forEach((error) => {
            if (error?.isAxiosError) {
              notifyError(error);
            } else {
              throw error;
            }
          });
        } finally {
          this.isAddingToCarts = false;
        }
      },

      /**
       * Emit to the parent when a variety is changed
       * @param Number id of the product to change to
       */
      changeVariety(product) {
        if (!product) {
          // Enable reverting to parent product details when variety was not pre-selected.
          if (!this.preselectedVarietyId) {
            this.selectedVariety = undefined;
          }

          return;
        }

        this.selectedVariety = product;
        this.$emit('change', product);
      },

      /**
       * Emit bulk-add to the parent
       */
      bulkAddToCart() {
        const { brandId, productId, companySlug } = this;

        this.$emit('bulk-add', { brandId, productId, companySlug });
      },

      /**
       * Handle quantity input change
       */
      onChangeQuantity(buyer) {
        if (!isEmpty(buyer.errors)) {
          buyer.errors = {};
        }
      },

      onRefreshProduct(product) {
        this.$emit('refresh-product', product);
      },

      closeModal() {
        this.$emit('close');
      },
    },
  };
</script>

<template>
  <ProductDetail
    :brand-id="brandId"
    :company-slug="companySlug"
    :product-id="productId"
    :is-preview="isPreview"
    :tracking-source="trackingSource"
    @set-product="product = $event"
    @close="$emit('close')"
    @refresh-product="onRefreshProduct"
  >
    <template v-if="product" #pricing>
      <!-- Pricing -->
      <div class="text-lowercase ll-mb-2">
        <!-- Parent With Varieties, No Variety Selected - Show Price Range -->
        <span v-if="showPriceRange" class="font-weight-medium heading-3">
          <!-- Same Units of Measure -->
          <template v-if="hasSameVarietyUnitsOfMeasure">
            {{ getVarietyPriceRange('effectivePrice', displayedProduct) }}/{{ displayedProduct.displayPriceUnit }}
          </template>
          <!-- Different Units of Measure -->
          <template v-else>
            {{ getVarietyPriceRange('effectivePrice', displayedProduct) }}
          </template>
        </span>
        <!-- Individual Item Pricing (Selected Variety or Standalone) -->
        <template v-else>
          <!-- Sale Price (With Original Wholesale/Base Price) -->
          <template v-if="displayedProduct.salePrice">
            <span class="color-ice heading-3 text-strike-through ll-mr-1">
              {{ money(displayedProduct.basePrice) }}
            </span>
            <span class="font-weight-medium heading-3">
              {{ money(displayedProduct.salePrice) }}/{{ displayedProduct.displayPriceUnit }}
            </span>
          </template>
          <!-- Wholesale/Base Price Only -->
          <span v-else class="font-weight-medium heading-3">
            {{ money(displayedProduct.basePrice) }}/{{ displayedProduct.displayPriceUnit }}
          </span>
        </template>

        <!-- Unit Pricing -->
        <div class="text-small">
          <!-- Price Per Unit In A Case -->
          <span v-if="showPricePerUnit" class="ll-mr-1">{{ unitPrice }}</span>
          <!-- Price Per Base Unit -->
          <span v-if="hasBaseUnitPricing(product)">
            <template v-if="showPriceRange">
              {{ getVarietyPriceRange('baseUnitPrice', displayedProduct) }}/{{
                displayedProduct.subCategory.baseUnitOfMeasureShortName
              }}
            </template>
            <template v-else>
              {{ money(displayedProduct.baseUnitPrice) }}/{{ displayedProduct.subCategory.baseUnitOfMeasureShortName }}
            </template>
          </span>
        </div>

        <Chip
          v-if="product.retailPrice.amount > 0"
          class="ll-mt-2"
          radius="standard"
          shade="light"
          color-scheme="ice"
          data-test="chip|msrp"
        >
          {{ $t('common.msrp') }}: {{ money(product.retailPrice) }}
        </Chip>
      </div>

      <div @click="closeModal()">
        <router-link
          class="ll-mb-3 button button--inline font-weight-medium"
          :to="{
            name: isPreview ? 'product-review' : 'product-details',
            params: { company: activeCompany.slug, productId: product.id },
            query: {
              segment_page_event_data: encodeSegmentTrackPageDataToQueryParams({
                data: {
                  algolia: {
                    query_id: algoliaQueryId,
                  },
                },
              }),
            },
          }"
        >
          {{ $t('brand_menu.viewProductDetail') }}
        </router-link>
      </div>
      <RequestSample v-if="canRequestSample" class="ll-mb-3" :product="displayedProduct" :source="source" />

      <Select
        v-if="product.children.length"
        single
        class="ll-mb-3"
        hide-search
        :prevent-empty="preselectedVarietyId"
        :label="$t('common.varieties')"
        :options="product.children"
        :model-value="selectedVariety"
        @update:model-value="changeVariety"
      >
        <template v-for="slotName in ['option', 'selected']" #[slotName]="{ option }" :key="`slot-name-${slotName}`">
          <div>
            <span class="ll-mr-2">{{ option.name }}</span>
            <Icon v-if="option.isFeaturedOnDeals && getIsCampaignLive" class="color-teal ll-mr-1" name="tag-star" />
            <Icon v-else-if="option.salePrice" class="color-red ll-mr-1" name="tag" />
            <Icon v-if="option.hasVolumeDiscount" class="color-orange" name="tag" />
          </div>
        </template>
      </Select>
      <div v-if="displayedProduct.volumeDiscounts.length" class="ll-mb-3" :title="$t('brand_menu.volumeDiscounts')">
        <h3 class="ll-mb-2 font-weight-medium">
          {{ $t('brand_menu.volumeDiscounts') }}
        </h3>
        <div class="bg-ice-200 ll-mb-1">
          <header
            class="ll-grid ll-grid-cols-4 overflow-hidden color-ice-900 font-weight-medium rounded ll-px-1 ll-py-2 bg-ice-100"
          >
            <div>{{ $t('common.quantity') }}</div>
            <div class="col-span-3">{{ $t('common.discount') }}</div>
          </header>
          <ul>
            <VolumeDiscountListItem
              v-for="(discount, index) in displayedProduct.volumeDiscounts"
              :key="`${index}_discount`"
              :discount="discount"
              :bulk="product.unitMultiplier > 1"
            />
          </ul>
        </div>
      </div>

      <template v-if="!companySlug">
        <div v-for="(buyer, index) in buyers" class="ll-mb-3" :key="index">
          <div class="flex align-middle">
            <Input
              v-model="buyer.inputs.quantity"
              data-test="modal|input-quantity"
              placeholder="0"
              type="number"
              :class="['flex-1 text-small', $style.input, { 'has-error': hasError(buyer, 'quantity') }]"
              :disabled="shouldDisableInputs"
              :label="$t('common.qty_short')"
              @update:model-value="onChangeQuantity(buyer)"
            />
            <Icon
              class="cursor-pointer ll-mt-2 ll-mx-2"
              data-test="modal|icon-add-notes"
              :name="buyer.inputs.notes ? 'note' : 'note-add'"
              @click="buyer.showNotes = !buyer.showNotes"
            />
            <Icon
              v-if="buyers.length > 1"
              class="cursor-pointer ll-mt-2 ll-mx-2"
              name="trashcan"
              @click="removeBuyers([buyer.id])"
            />
          </div>

          <div class="ll-mt-1 ll-mb-3 color-ice-700 text-small">
            <span class="block">
              {{ $t('common.min') }}: <span data-test="modal|min-quantity">{{ displayedProduct.displayMinOrder }}</span>
            </span>
            <span class="block">
              {{ $t('common.max') }}:
              <span data-test="modal|max-quantity">{{
                displayedProduct.displayMaxOrder ? displayedProduct.displayMaxOrder : '-'
              }}</span>
            </span>
          </div>

          <Input
            v-if="buyer.showNotes"
            v-model="buyer.inputs.notes"
            data-test="modal|input-notes"
            placeholder="—"
            :class="['ll-mb-3 full-width text-small', $style.input, { 'has-error': hasError(buyer, 'notes') }]"
            :disabled="shouldDisableInputs"
            :label="$t('common.notes')"
          />

          <div
            v-if="formattedErrors(buyer).length"
            v-html="formattedErrors(buyer).join('<br />')"
            class="ll-mb-3 color-red"
          ></div>

          <LicensePicker
            v-if="allowedBuyers.length > 1"
            class="ll-mb-3"
            :label="$t('common.license')"
            :model-value="companies.find((company) => company.id === buyer.id)"
            :options="getBuyerOptions(buyer)"
            @update:model-value="(company) => (buyer.id = company ? company.id : '')"
          />
          <hr v-if="index + 1 !== buyers.length" :class="$style.divider" />
        </div>

        <Button v-if="allowedBuyers.length > buyers.length" inline class="ll-mb-3" @click="addNewBuyer">
          <Icon size="small" name="plus" />
          {{ $t('shop.addAnotherLocation') }}
        </Button>

        <div>
          <Button class="w-full" data-test="modal|add-to-cart" :disabled="!canAddToCart" @click="addProductToCarts">
            <template v-if="displayedProduct.shouldBeVisible">
              {{ $t('brand_menu.addToCart') }}
            </template>
            <template v-else>
              {{ $t('common.currentlyUnavailable') }}
            </template>
          </Button>
        </div>
      </template>

      <div v-else>
        <Button class="w-full" @click="bulkAddToCart">
          {{ $t('shop.addToCart') }}
        </Button>
      </div>
    </template>
  </ProductDetail>
</template>

<style lang="scss" module>
  .input {
    :global(.input-label) {
      line-height: inherit;
    }
  }

  .divider {
    border-bottom: get($borders, 'medium') solid var(--color-ice);
  }
</style>
